import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import { Margin } from 'styled-components-spacing';
import { queryTypeOptions } from '../../CisOptions';
import { exportCertificatesReportToExcel } from 'api/services/cis';
import moment from 'moment';

const ExportCertificatesReport = ({
  state,
  updateFieldCreator,
  dispatch,
  loadCertificatesAttributes,
  loadClientMemberAssociations,
  memberAssociations,
  certificationBodies,
  countries,
  type,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const isCvmbCis = history.location?.pathname?.includes('cvmb-cis') ?? false;

  const handleChangeCustom = (name) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
  };

  const handleChangeSelect = (name) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
  };

  const submit = async () => {
    setLoading(true);
    const params = {
      dateFrom: state.startDate ? moment(state.startDate).format('YYYY-MM-DD') : undefined,
      dateTo: state.endDate ? moment(state.endDate).format('YYYY-MM-DD') : undefined,
      queryType: state.queryType,
      memberAssociation: state.memberAssociation,
      certificationBody: state.certificationBody,
      country: state.country,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== null && v !== undefined),
    );
    await exportCertificatesReportToExcel(filteredParams);
    setLoading(false);
    if (isCvmbCis) {
      history.push(`/ipma/cvmb-cis/statistics/certificates`);
    } else {
      history.push(`/${type}/cis/certificates`);
    }
  };

  useEffect(() => {
    loadCertificatesAttributes();
  }, []);

  useEffect(() => {
    if (state.certificationBody) {
      loadClientMemberAssociations(state.certificationBody);
    }
  }, [state.certificationBody]);

  return (
    <PageContent>
      <Container>
        <form onSubmit={submit}>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificates.exportReport')}</Heading>
          </Margin>
          {(type === 'ipma' || isCvmbCis) && (
            <>
              <Row>
                <Col size={{ xl: 1 / 2 }}>
                  <Margin bottom="20">
                    <Field
                      id="certificationBody"
                      label={t('page.user.cis.certificates.certificationBody')}
                    >
                      <Select
                        id="certificationBody"
                        options={certificationBodies}
                        value={
                          certificationBodies?.find((o) => o.value === state.certificationBody) ||
                          ''
                        }
                        onChange={handleChangeSelect('certificationBody')}
                        placeholder={t('page.user.cis.certificates.certificationBodyPlaceholder')}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 2 }}>
                  <Margin bottom="20">
                    <Field
                      id="memberAssociation"
                      label={t('page.user.cis.certificates.clientMemberAssociation')}
                    >
                      <Select
                        id="memberAssociation"
                        options={memberAssociations}
                        value={
                          memberAssociations?.find((o) => o.value === state.memberAssociation) || ''
                        }
                        onChange={handleChangeSelect('memberAssociation')}
                        placeholder={t(
                          'page.user.cis.certificates.clientMemberAssociationPlaceholder',
                        )}
                        disabled={
                          !state.certificationBody ||
                          !memberAssociations ||
                          memberAssociations.length === 0
                        }
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
              <Row>
                <Col size={{ xl: 1 / 2 }}>
                  <Margin bottom="20">
                    <Field id="country" label={t('page.user.cis.certificates.clientCountry')}>
                      <Select
                        id="country"
                        options={countries}
                        value={countries?.find((o) => o.value === state.country) || ''}
                        onChange={handleChangeSelect('country')}
                        placeholder={t('page.user.cis.certificates.clientCountryPlaceholder')}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 2 }}>
                  <Margin bottom="20">
                    <Field id="queryType" label={t('page.user.cis.certificates.queryType')}>
                      <Select
                        id="queryType"
                        options={queryTypeOptions}
                        value={queryTypeOptions.find((o) => o.value === state.queryType) || ''}
                        onChange={handleChangeSelect('queryType')}
                        placeholder={t('page.user.cis.certificates.queryTypePlaceholder')}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col size={{ xl: 1 / 2 }}>
              <Margin bottom="20">
                <Field id="startDate" label={t('page.user.cis.certificates.startDate')}>
                  <Datepicker
                    id="startDate"
                    selected={state.startDate}
                    onChange={handleChangeCustom('startDate')}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={t('page.user.cis.certificates.startDatePlaceholder')}
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 2 }}>
              <Margin bottom="20">
                <Field id="endDate" label={t('page.user.cis.certificates.endDate')}>
                  <Datepicker
                    id="endDate"
                    selected={state.endDate}
                    onChange={handleChangeCustom('endDate')}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={t('page.user.cis.certificates.endDatePlaceholder')}
                    minDate={state.startDate}
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          {type === 'ma' && (
            <Row>
              <Col size={{ xl: 1 / 2 }}>
                <Margin bottom="20">
                  <Field id="queryType" label={t('page.user.cis.certificates.queryType')}>
                    <Select
                      id="queryType"
                      options={queryTypeOptions}
                      value={queryTypeOptions.find((o) => o.value === state.queryType) || ''}
                      onChange={handleChangeSelect('queryType')}
                      placeholder={t('page.user.cis.certificates.queryTypePlaceholder')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Button type="submit" disabled={loading} onClick={() => submit()}>
                {loading ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

ExportCertificatesReport.propTypes = {
  state: PropTypes.shape({
    memberAssociation: PropTypes.number,
    certificationBody: PropTypes.number,
    country: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    queryType: PropTypes.string,
  }),
  updateFieldCreator: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  loadCertificatesAttributes: PropTypes.func.isRequired,
  loadClientMemberAssociations: PropTypes.func.isRequired,
  memberAssociations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  certificationBodies: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  type: PropTypes.oneOf(['ipma', 'ma']),
};

ExportCertificatesReport.defaultProps = {
  type: 'ipma',
};

export default ExportCertificatesReport;
